import React, { useEffect, useRef, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import * as styles from './UserEngagement.module.scss';
import { MdClose, MdKeyboardArrowDown } from 'react-icons/md';
import { MantineProvider, MultiSelect, Select, Textarea, TextInput } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { emailValidation } from '../../lib/email-validation';
import ButtonComponent from '../Home/ButtonComponent/ButtonComponent';
import { ndeFormInstance } from '../../lib/axios-client';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { toast } from 'react-toastify';

const schema = yup.object({
  name: yup
    .string()
    .required('Name is required.')
    .transform(value => value.trim())
    .min(3, 'Name must contain at least 3 letters.'),
  email: emailValidation,
  phone: yup
    .string()
    .required('Phone number is required.')
    .matches(/^[+]?[0-9\s\-()]{10,15}$/, 'Please enter a valid mobile number.'),
  service_items: yup.array().of(yup.string()).optional(),
  budget: yup.string().required('Budget is required!'),
  message: yup
    .string()
    .required('Please provide your message.')
    .min(10, 'Message should at least be 10 characters long.')
    .max(300, 'Message cannot exceed 300 characters.'),
});

export default function UserEngagement() {
  // Budget copy/options are managed in WP: Theme General Settings → Contact Popup Form
  const popupData = useStaticQuery(graphql`
    query UserEngagementBudget {
      wp {
        themeGeneralSettings {
          contactPopupForm {
            budgetPlaceholder
            budgetOptions {
              label
            }
          }
        }
      }
    }
  `)?.wp?.themeGeneralSettings?.contactPopupForm;

  const budgetOptions = (popupData?.budgetOptions || [])
    .map(option => option?.label)
    .filter(Boolean);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [opened, { open, close }] = useDisclosure();
  const [apiLoading, setApiLoading] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const eligibleForExitIntent = useRef(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const triggerModal = () => {
    if (!hasMounted) return;

    const hasShown = sessionStorage.getItem('contact_form');
    const isDisabled = sessionStorage.getItem('userEngagementFormDisabled');
    const formModalOpen = sessionStorage.getItem('formOpen');

    if (!hasShown && !isDisabled && !formModalOpen) {
      open();
      sessionStorage.setItem('contact_form', 'true');
    }
  };

  const onSubmit = async (data: {
    email: string;
    name: string;
    phone: string;
    message: string;
    service_items?: (string | undefined)[] | undefined;
    budget?: string;
  }) => {
    setApiLoading(true);
    try {
      const landing_page = sessionStorage.getItem('landing_page');
      await ndeFormInstance.post('/lead-tracking', {
        ...data,
        service_items: data.service_items?.filter(
          (item): item is string => typeof item === 'string'
        ),
        form_id: 'close-intent-popup',
        landing_page,
      });
      if ((window as any).gtag) {
        (window as any).gtag('event', 'contact_form_submitted');
      }
      close();
      navigate('/thank-you');
    } catch (error: any) {
      toast.error(error.message);
      console.error(error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    if (!hasMounted) return;

    // After 15 seconds on the site the visitor becomes eligible for the exit-intent popup
    const timer = window.setTimeout(() => {
      eligibleForExitIntent.current = true;
    }, 15000);

    return () => window.clearTimeout(timer);
  }, [hasMounted]);

  useEffect(() => {
    if (!hasMounted) return;

    // Show the popup only once the visitor has stayed 15s AND moves the cursor outside the window
    const handleMouseLeave = (e: MouseEvent) => {
      if (!eligibleForExitIntent.current) return;
      if (
        e.clientY <= 0 ||
        e.clientX <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        triggerModal();
      }
    };

    window.addEventListener('mouseout', handleMouseLeave);
    return () => window.removeEventListener('mouseout', handleMouseLeave);
  }, [hasMounted]);

  return (
    <MantineProvider>
      {opened && (
        <div className={styles.UserEngagement__backdrop}>
          <div className={styles.UserEngagement__container}>
            <button className={styles.UserEngagement__closeButton} onClick={close}>
              Close <MdClose size={20} />
            </button>
            <div className={styles.UserEngagement__modal}>
              <div className={styles.UserEngagement__heading}>
                Your next big idea deserves better design, talk to us!!
              </div>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className={styles.UserEngagement__formContainer}
              >
                <TextInput
                  placeholder='What is your name? *'
                  size='lg'
                  className={styles.UserEngagement__fieldArea}
                  {...register('name')}
                  error={errors.name?.message}
                />

                <div className={styles.UserEngagement__rowContainer}>
                  <TextInput
                    placeholder='Your email for contact *'
                    size='lg'
                    className={styles.UserEngagement__halfFieldArea}
                    {...register('email')}
                    error={errors.email?.message}
                  />

                  <TextInput
                    placeholder='Mobile number *'
                    size='lg'
                    className={styles.UserEngagement__halfFieldArea}
                    {...register('phone')}
                    error={errors.phone?.message}
                  />
                </div>

                <Controller
                  control={control}
                  name='service_items'
                  render={({ field }) => (
                    <MultiSelect
                      data={[
                        'Website Design',
                        'Mobile App Design',
                        'SaaS Product Design',
                        'eCommerce',
                        'UI/UX Audit',
                        'Other',
                      ]}
                      placeholder='Project Type'
                      size='lg'
                      className={styles.UserEngagement__serviceItems}
                      classNames={{
                        input: styles.inputField,
                        option: styles.multiValue,
                        pill: styles.pills,
                        section: styles.UserEngagement__margin,
                      }}
                      rightSection={<MdKeyboardArrowDown size={20} />}
                      onChange={field.onChange}
                      error={errors.service_items?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name='budget'
                  render={({ field }) => (
                    <Select
                      data={budgetOptions}
                      placeholder={popupData?.budgetPlaceholder || 'Budget'}
                      size='lg'
                      className={styles.UserEngagement__serviceItems}
                      classNames={{
                        input: `${styles.inputField} ${styles.budgetInput}`,
                        option: styles.multiValue,
                        section: styles.UserEngagement__margin,
                      }}
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      comboboxProps={{ zIndex: 1000 }}
                      rightSection={<MdKeyboardArrowDown size={20} />}
                      error={errors.budget?.message}
                    />
                  )}
                />

                <div className={styles.UserEngagement__textareaContainer}>
                  <Textarea
                    classNames={{
                      input: styles.messageField,
                    }}
                    placeholder='Message *'
                    error={errors.message?.message}
                    {...register('message', {
                      onChange: e => setMessageLength(e.target.value.length),
                    })}
                  />
                  <div className={styles.UserEngagement__characterCount}>
                    {messageLength}/300 characters
                  </div>
                </div>

                <ButtonComponent
                  buttonText={'Submit'}
                  buttonStyle={'solidBg'}
                  type='submit'
                  className={styles.UserEngagement__submit_button}
                  onClick={undefined}
                  disabled={undefined}
                  loading={apiLoading}
                />
              </form>
            </div>
          </div>
        </div>
      )}
    </MantineProvider>
  );
}
